import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { v1Users, v1Tasks, type V1Admin } from '../../../lib/v1';
import { type TaskRow } from './types';
import { fmtDt, toLocalInput, currentAdminId, adminLabel, assigneeNames } from './helpers';
import { WidgetCard } from './WidgetCard';
import { TaskStatusSelect, TaskInProgressBadge } from './TaskStatus';
import { TaskPriorityToggle, TaskPriorityBadge, highPriorityRowStyle } from './TaskPriority';
import { AssigneeSelect } from './AssigneeSelect';
import { DateTimeLocalInput } from '../../../components/DateTimeInput';

/* ─── AufgabenWidget (compact) ────────────────────────────────────────────── */

export function AufgabenWidget({ dark, userId, admins, onShowAll, onEditTask, reloadRef, onChanged }: { dark: boolean; userId: number; admins: V1Admin[]; onShowAll: (e: React.MouseEvent) => void; onEditTask?: (task: TaskRow) => void; reloadRef?: React.MutableRefObject<(() => void) | null>; onChanged?: () => void }) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [zieldatum, setZieldatum] = useState('');
  const [bearbeiter, setBearbeiter] = useState<number[]>(() => {
    const me = currentAdminId();
    return me != null ? [me] : [];
  });
  const [formError, setFormError] = useState<string | null>(null);
  // Priorität der neuen Aufgabe (0 = Normal, 1 = Hoch).
  const [priority, setPriority] = useState(0);
  const titelRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(() => {
    setLoading(true);
    v1Users.tasks(userId, { perPage: 5, filters: { showFinishedTasks: 'false' } })
      .then(p => setTasks((p.data as unknown as TaskRow[]).slice(0, 5)))
      .catch(() => {/* ignore */})
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (reloadRef) reloadRef.current = reload;
    return () => { if (reloadRef) reloadRef.current = null; };
  }, [reloadRef, reload]);

  const handleToggle = useCallback(async (task: TaskRow, e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus = task.status === 1 ? 0 : 1;
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    try {
      await v1Tasks.update(task.id, { titel: task.titel ?? '(Kein Titel)', beschreibung: task.beschreibung ?? '', status: newStatus });
      onChanged?.();
    } catch {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
    }
  }, [onChanged]);

  const handleAdd = useCallback(async (e: React.FormEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!titel.trim()) { setFormError(t('pflege.detail.dashboard.task_titel_required')); return; }
    if (!beschreibung.trim()) { setFormError(t('pflege.detail.dashboard.task_beschr_required')); return; }
    if (!zieldatum) { setFormError(t('pflege.detail.dashboard.task_date_required')); return; }
    setSaving(true); setFormError(null);
    try {
      const me = currentAdminId();
      const created = await v1Tasks.create({
        user_id: userId,
        titel: titel.trim(),
        beschreibung: beschreibung.trim(),
        zieldatum: zieldatum || null,
        priority,
        status: 0,
        von: me,
        bearbeiter: bearbeiter.length ? bearbeiter : null,
      });
      setTasks(prev => [created as TaskRow, ...prev].slice(0, 5));
      setTitel(''); setBeschreibung(''); setZieldatum('');
      setPriority(0);
      setBearbeiter(me != null ? [me] : []);
      setShowForm(false);
      onChanged?.();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('pflege.detail.dashboard.save_error'));
    } finally { setSaving(false); }
  }, [userId, titel, beschreibung, zieldatum, bearbeiter, priority, t, onChanged]);

  return (
    <WidgetCard dark={dark} title={t('pflege.detail.dashboard.aufgaben')} badge={tasks.length || null} onShowAll={onShowAll} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')} loading={loading}>
      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
        {!loading && tasks.length === 0 && !showForm && (
          <div style={{ padding: '10px 14px', fontSize: 12, color: tc.muted, fontStyle: 'italic' }}>{t('pflege.tasks.empty')}</div>
        )}
        {tasks.map(task => {
          const done = task.status === 1;
          return (
            <div
              key={task.id}
              role="button"
              tabIndex={0}
              aria-label={`${t('pflege.detail.dashboard.aufgaben')}: ${task.titel ?? t('pflege.tasks.no_title')}`}
              onClick={(e) => { e.stopPropagation(); onEditTask?.(task); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onEditTask?.(task); } }}
              style={{ padding: '8px 14px', borderBottom: `1px solid ${tc.borderSoft}`, display: 'flex', alignItems: 'flex-start', gap: 10, opacity: done ? 0.55 : 1, cursor: onEditTask ? 'pointer' : 'default', ...highPriorityRowStyle(task, dark) }}
            >
              <button type="button" title={done ? t('pflege.detail.dashboard.task_reopen') : t('pflege.detail.dashboard.task_done')} onClick={(e) => handleToggle(task, e)}
                style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', border: `1.5px solid ${done ? '#12b76a' : tc.border}`, background: done ? '#12b76a' : 'transparent', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}
              >{done ? '✓' : ''}</button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: tc.heading, textDecoration: done ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.titel ?? t('pflege.tasks.no_title')}</div>
                {(() => {
                  const names = assigneeNames(task.bearbeiter as number[] | null | undefined, admins);
                  return names ? (
                    <div style={{ fontSize: 10.5, color: BRAND, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t('pflege.detail.dashboard.task_assignee_prefix')} {names}
                    </div>
                  ) : null;
                })()}
                {task.created_at && <div style={{ fontSize: 10.5, color: tc.muted, marginTop: 1 }}>{fmtDt(task.created_at)}</div>}
              </div>
              <TaskPriorityBadge task={task} />
              <TaskInProgressBadge status={task.status} />
              {onEditTask && <span style={{ fontSize: 10, color: tc.muted, flexShrink: 0 }}>✏</span>}
            </div>
          );
        })}
      </div>
      {showForm ? (
        <form onSubmit={handleAdd} onClick={(e) => e.stopPropagation()} style={{ borderTop: `1px solid ${tc.border}`, padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input ref={titelRef} type="text" placeholder={t('pflege.detail.dashboard.task_titel_placeholder')} value={titel} onChange={e => setTitel(e.target.value)} onClick={e => e.stopPropagation()} style={{ fontSize: 12.5, padding: '6px 10px', border: `1px solid ${tc.border}`, borderRadius: 7, background: tc.inputBg, color: tc.text, outline: 'none' }} />
          <input type="text" placeholder={t('pflege.detail.dashboard.task_beschr_placeholder')} value={beschreibung} onChange={e => setBeschreibung(e.target.value)} onClick={e => e.stopPropagation()} style={{ fontSize: 12.5, padding: '6px 10px', border: `1px solid ${tc.border}`, borderRadius: 7, background: tc.inputBg, color: tc.text, outline: 'none' }} />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 10.5, color: tc.muted }}>
            {t('pflege.detail.dashboard.task_date_label')}
            <span onClick={e => e.stopPropagation()}><DateTimeLocalInput ariaLabel={t('pflege.detail.dashboard.task_date_label')} value={zieldatum} onChange={setZieldatum} style={{ fontSize: 12.5, padding: '6px 10px', border: `1px solid ${tc.border}`, borderRadius: 7, background: tc.inputBg, color: zieldatum ? tc.text : tc.muted, outline: 'none' }} /></span>
          </label>
          <AssigneeSelect dark={dark} admins={admins} value={bearbeiter} onChange={setBearbeiter} compact />
          <TaskPriorityToggle dark={dark} value={priority} onChange={setPriority} compact />
          {formError && <div style={{ fontSize: 11, color: '#b42318' }}>{formError}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="submit" disabled={saving} style={{ flex: 1, fontSize: 12, fontWeight: 600, background: BRAND, color: '#fff', border: 'none', borderRadius: 7, padding: '6px 0', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>{saving ? '…' : t('pflege.detail.dashboard.hinzufuegen')}</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); setShowForm(false); setTitel(''); setBeschreibung(''); setZieldatum(''); setFormError(null); }} style={{ fontSize: 12, padding: '6px 10px', background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, color: tc.muted, cursor: 'pointer' }}>{t('common.action.cancel')}</button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={(e) => { e.stopPropagation(); setShowForm(true); setTimeout(() => titelRef.current?.focus(), 50); }} style={{ margin: '8px 14px 10px', fontSize: 12, fontWeight: 600, color: BRAND, background: BRAND_LIGHT, border: `1px dashed ${BRAND}`, borderRadius: 7, padding: '5px 10px', cursor: 'pointer', textAlign: 'left' }}>+ {t('pflege.detail.dashboard.aufgabe_hinzufuegen')}</button>
      )}
    </WidgetCard>
  );
}

/* ─── Inline TaskEditForm im Drawer ─────────────────────────────────────── */

export interface TaskEditFormProps {
  dark: boolean;
  task: TaskRow;
  userId: number;
  admins: V1Admin[];
  onSaved: (updated: TaskRow) => void;
  onCancel: () => void;
}

export function TaskEditForm({ dark, task, admins, onSaved, onCancel }: TaskEditFormProps) {
  const tc = getThemeColors(dark);
  const { t } = useTranslation();
  const [titel, setTitel] = useState(task.titel ?? '');
  const [beschreibung, setBeschreibung] = useState(task.beschreibung ?? '');
  const [zieldatum, setZieldatum] = useState(toLocalInput(task.zieldatum));
  // Nur eine zuständige Person: Bestandsaufgaben mit mehreren behalten beim
  // Bearbeiten die erste.
  const [bearbeiter, setBearbeiter] = useState<number[]>(
    Array.isArray(task.bearbeiter) ? (task.bearbeiter as number[]).slice(0, 1) : [],
  );
  // Status: Offen / In Bearbeitung / Erledigt (wie CRM 1.0).
  const [status, setStatus] = useState<number>(task.status ?? 0);
  // Priorität: 0 = Normal, 1 = Hoch (Alarm-Rot).
  const [priority, setPriority] = useState<number>(Number(task.priority ?? 0));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titel.trim()) { setError(t('pflege.detail.dashboard.task_titel_required')); return; }
    if (!beschreibung.trim()) { setError(t('pflege.detail.dashboard.task_beschr_required')); return; }
    setSaving(true); setError(null);
    try {
      const updated = await v1Tasks.update(task.id, {
        titel: titel.trim(),
        beschreibung: beschreibung.trim(),
        status,
        priority,
        zieldatum: zieldatum || null,
        bearbeiter: bearbeiter.length ? bearbeiter : null,
      });
      onSaved(updated as TaskRow);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pflege.detail.dashboard.save_error'));
    } finally { setSaving(false); }
  }, [titel, beschreibung, zieldatum, bearbeiter, status, priority, task, t, onSaved]);

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '8px 12px', width: '100%', boxSizing: 'border-box',
    border: `1px solid ${tc.border}`, borderRadius: 8,
    background: tc.inputBg, color: tc.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: tc.muted, display: 'block', marginBottom: 5,
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <label style={labelStyle}>{t('pflege.detail.dashboard.task_titel_placeholder')}</label>
        <input type="text" value={titel} onChange={e => setTitel(e.target.value)} style={inputStyle} />
      </div>
      <div>
        <label style={labelStyle}>{t('pflege.detail.dashboard.task_beschr_placeholder')}</label>
        <input type="text" value={beschreibung} onChange={e => setBeschreibung(e.target.value)} style={inputStyle} />
      </div>
      <div>
        <label style={labelStyle}>{t('pflege.detail.dashboard.task_date_label')}</label>
        <DateTimeLocalInput ariaLabel={t('pflege.detail.dashboard.task_date_label')} value={zieldatum} onChange={setZieldatum} style={inputStyle} />
      </div>
      <AssigneeSelect dark={dark} admins={admins} value={bearbeiter} onChange={setBearbeiter} />
      <TaskStatusSelect dark={dark} value={status} onChange={setStatus} />
      <TaskPriorityToggle dark={dark} value={priority} onChange={setPriority} />
      {task.von != null && (() => {
        const vonId = typeof task.von === 'number' ? task.von : Number(task.von);
        const creator = admins.find(a => a.id === vonId);
        return (
          <div style={{ fontSize: 11.5, color: tc.muted }}>
            {t('pflege.detail.dashboard.task_creator_prefix')} {creator ? adminLabel(creator) : `#${task.von}`}
          </div>
        );
      })()}
      {error && (
        <div style={{ padding: '8px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 7, fontSize: 12.5, color: '#b42318' }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="submit" disabled={saving} style={{ flex: 1, fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'Outfit,sans-serif' }}>
          {saving ? '…' : t('common.action.save')}
        </button>
        <button type="button" onClick={onCancel} style={{ fontSize: 13, padding: '10px 18px', background: 'none', border: `1px solid ${tc.border}`, borderRadius: 8, color: tc.muted, cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}>
          {t('common.action.cancel')}
        </button>
      </div>
    </form>
  );
}
